import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { HttpProblemError, type LanguageCode, type User } from '@collector/shared'
import { api } from './api'
import { i18n, isSupportedLanguage } from './i18n'
import { logError } from './log'

interface AuthState {
  status: 'loading' | 'anonymous' | 'authenticated'
  user: User | null
}

interface AuthContextValue extends AuthState {
  login: (username: string, password: string) => Promise<User>
  logout: () => Promise<void>
  refresh: () => Promise<void>
  /**
   * Change the active UI language. Persists to the server when a user is
   * authenticated so the preference follows them across devices; always
   * updates i18next locally (which in turn writes to localStorage).
   */
  setLanguage: (lang: LanguageCode) => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading', user: null })

  const applyUserLanguage = useCallback((user: User) => {
    if (user.language && isSupportedLanguage(user.language) && i18n.language !== user.language) {
      void i18n.changeLanguage(user.language)
    }
  }, [])

  const refresh = useCallback(async () => {
    try {
      const user = await api().auth.me()
      applyUserLanguage(user)
      setState({ status: 'authenticated', user })
    } catch (err) {
      if (err instanceof HttpProblemError && err.status === 401) {
        setState({ status: 'anonymous', user: null })
        return
      }
      logError('auth.refresh failed', err)
      setState({ status: 'anonymous', user: null })
    }
  }, [applyUserLanguage])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const login = useCallback(
    async (username: string, password: string) => {
      const user = await api().auth.login({ username, password })
      applyUserLanguage(user)
      setState({ status: 'authenticated', user })
      return user
    },
    [applyUserLanguage],
  )

  const logout = useCallback(async () => {
    try {
      await api().auth.logout()
    } catch (err) {
      logError('auth.logout failed', err)
    } finally {
      setState({ status: 'anonymous', user: null })
    }
  }, [])

  const setLanguage = useCallback(async (lang: LanguageCode) => {
    // Switch the UI immediately so the operator sees feedback even if the
    // network save is slow or the user is anonymous.
    await i18n.changeLanguage(lang)
    setState((prev) =>
      prev.status === 'authenticated' && prev.user
        ? { ...prev, user: { ...prev.user, language: lang } }
        : prev,
    )
    // Best-effort persistence: don't block the UI on a failed write — the
    // local change is already visible. Log errors so regressions don't go
    // silent.
    try {
      const updated = await api().auth.patchMe({ language: lang })
      setState((prev) => (prev.status === 'authenticated' ? { ...prev, user: updated } : prev))
    } catch (err) {
      if (!(err instanceof HttpProblemError && err.status === 401)) {
        logError('auth.setLanguage failed', err, { lang })
      }
    }
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, login, logout, refresh, setLanguage }),
    [state, login, logout, refresh, setLanguage],
  )
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
